import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, Mail, ShieldCheck, Smartphone } from 'lucide-react'
import { Button, InlineError, Input, cn } from '@asad/crane-ui'

import { MfaCodeInput } from './MfaCodeInput'

/** Kroki weryfikacji MFA przy logowaniu — prezentacja przeniesiona z
 *  `app/prototypes/src/components/auth/MfaSteps.tsx`. Komponenty są czysto
 *  prezentacyjne: przyjmują wartości i callbacki, nie znają Reduxa ani API.
 *  Konsumenci: `MfaVerify` (trasa /auth/mfa-verify) oraz modale MFA w profilu. */

/* ── MfaStepHeader ──────────────────────────────────────────────────────── */

export type MfaStepHeaderProps = {
  icon: ReactNode
  iconContainerClass?: string
  title: string
  subtitle?: string
  subtitleClass?: string
  marginBottom?: string
}

export const MfaStepHeader = ({
  icon,
  iconContainerClass,
  title,
  subtitle,
  subtitleClass,
  marginBottom,
}: MfaStepHeaderProps) => (
  <div className={cn('flex flex-col items-center gap-1 text-center', marginBottom ?? 'mb-4')}>
    <span
      className={cn(
        'mb-1.5 grid size-11 place-items-center rounded-crane-md bg-crane-primary/15 text-crane-primary',
        iconContainerClass,
      )}
      aria-hidden
    >
      {icon}
    </span>
    <h2 className='text-crane-2xl font-semibold leading-5 text-crane-foreground'>{title}</h2>
    {subtitle ? (
      <p className={cn('text-xs leading-snug text-crane-muted-foreground', subtitleClass)}>{subtitle}</p>
    ) : null}
  </div>
)

/** Przycisk-link pod formularzem kroku MFA. */
export const MfaLinkButton = ({
  muted,
  disabled,
  onClick,
  children,
}: {
  muted?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) => (
  <button
    type='button'
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'min-h-11 max-w-full px-2 py-2 text-xs font-semibold transition-colors hover:underline md:min-h-0 md:p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring',
      muted ? 'text-crane-muted-foreground' : 'text-crane-primary',
      disabled && 'cursor-default text-crane-muted-foreground hover:no-underline',
    )}
  >
    {children}
  </button>
)

/** Kafel wyboru metody MFA (wspólny dla verify i setup). */
export const MfaMethodButton = ({
  icon,
  label,
  description,
  disabled,
  onClick,
}: {
  icon: ReactNode
  label: string
  description?: string
  disabled?: boolean
  onClick: () => void
}) => (
  <button
    type='button'
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex w-full items-center gap-3 rounded-crane-md bg-crane-card-inset p-3.5 text-left transition-colors',
      'hover:bg-crane-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring',
      'disabled:cursor-not-allowed disabled:opacity-60',
    )}
  >
    <span
      className='grid size-9 shrink-0 place-items-center rounded-crane-md bg-crane-primary/15 text-crane-primary'
      aria-hidden
    >
      {icon}
    </span>
    <span className='min-w-0'>
      <span className='block text-crane-xl font-semibold text-crane-foreground'>{label}</span>
      {description ? (
        <span className='block text-crane-sm leading-snug text-crane-muted-foreground'>{description}</span>
      ) : null}
    </span>
  </button>
)

/* ── MfaMethodSelectStep ────────────────────────────────────────────────── */

export type MfaMethodSelectStepProps = {
  isSendingEmail: boolean
  onSelectEmail: () => void
  onSelectTotp?: () => void
}

export const MfaMethodSelectStep = ({ isSendingEmail, onSelectEmail, onSelectTotp }: MfaMethodSelectStepProps) => {
  const { t } = useTranslation()

  return (
    <div>
      <MfaStepHeader
        icon={<ShieldCheck className='size-5' aria-hidden />}
        title={t('mfaVerify.title')}
        subtitle={t('mfaVerify.methodSelectTitle')}
      />
      <div className='flex flex-col gap-2.5'>
        {onSelectTotp ? (
          <MfaMethodButton
            icon={<Smartphone className='size-4' aria-hidden />}
            label={t('mfaVerify.methodTotp')}
            onClick={onSelectTotp}
          />
        ) : null}
        <MfaMethodButton
          icon={<Mail className='size-4' aria-hidden />}
          label={isSendingEmail ? t('mfaVerify.sendingEmail') : t('mfaVerify.methodEmail')}
          disabled={isSendingEmail}
          onClick={onSelectEmail}
        />
      </div>
    </div>
  )
}

/* ── MfaCodeStep (TOTP / EMAIL) ─────────────────────────────────────────── */

export type MfaCodeStepProps = {
  method: 'TOTP' | 'EMAIL'
  code: string
  setCode: (code: string) => void
  isSubmitting: boolean
  isSendingEmail?: boolean
  resendSecondsLeft?: number
  hasMultipleMethods: boolean
  onVerify: () => void
  onResend?: () => void
  onGoBackupStep: () => void
  onGoSelectStep?: () => void
  onGoBack?: (() => void) | null
  error?: string
}

export const MfaCodeStep = ({
  method,
  code,
  setCode,
  isSubmitting,
  isSendingEmail,
  resendSecondsLeft = 0,
  hasMultipleMethods,
  onVerify,
  onResend,
  onGoBackupStep,
  onGoSelectStep,
  onGoBack,
  error,
}: MfaCodeStepProps) => {
  const { t } = useTranslation()

  return (
    <form
      className='space-y-3.5'
      onSubmit={(event) => {
        event.preventDefault()
        onVerify()
      }}
      noValidate
    >
      <MfaStepHeader
        icon={
          method === 'EMAIL' ? <Mail className='size-5' aria-hidden /> : <ShieldCheck className='size-5' aria-hidden />
        }
        title={t('mfaVerify.title')}
        subtitle={method === 'EMAIL' ? t('mfaVerify.emailSubtitle') : t('mfaVerify.totpSubtitle')}
      />

      <MfaCodeInput value={code} onChange={setCode} />
      {error ? <InlineError>{error}</InlineError> : null}

      <Button
        type='submit'
        className='min-h-11 w-full whitespace-normal md:min-h-0 md:whitespace-nowrap'
        disabled={code.length !== 6}
        loading={isSubmitting}
        loadingLabel={t('mfaVerify.verifying')}
      >
        {t('mfaVerify.submitButton')}
      </Button>

      <div className='flex flex-col items-center gap-2 pt-0.5'>
        {method === 'EMAIL' && onResend ? (
          <MfaLinkButton disabled={resendSecondsLeft > 0 || isSendingEmail} onClick={onResend}>
            {resendSecondsLeft > 0
              ? t('mfaVerify.resendCooldown', { seconds: resendSecondsLeft })
              : t('mfaVerify.resend')}
          </MfaLinkButton>
        ) : null}
        <MfaLinkButton onClick={onGoBackupStep}>{t('mfaVerify.backupCodeLink')}</MfaLinkButton>
        {hasMultipleMethods && onGoSelectStep ? (
          <MfaLinkButton onClick={onGoSelectStep}>{t('mfaVerify.backToSelect')}</MfaLinkButton>
        ) : null}
        {onGoBack ? (
          <MfaLinkButton muted onClick={onGoBack}>
            {t('mfaVerify.backToLogin')}
          </MfaLinkButton>
        ) : null}
      </div>
    </form>
  )
}

/* ── MfaBackupStep ──────────────────────────────────────────────────────── */

export type MfaBackupStepProps = {
  backupCode: string
  setBackupCode: (code: string) => void
  isSubmitting: boolean
  onVerify: () => void
  onGoBack?: (() => void) | null
  error?: string
}

export const MfaBackupStep = ({
  backupCode,
  setBackupCode,
  isSubmitting,
  onVerify,
  onGoBack,
  error,
}: MfaBackupStepProps) => {
  const { t } = useTranslation()

  return (
    <form
      className='space-y-3.5'
      onSubmit={(event) => {
        event.preventDefault()
        onVerify()
      }}
      noValidate
    >
      <MfaStepHeader
        icon={<KeyRound className='size-5' aria-hidden />}
        title={t('mfaVerify.backupTitle')}
        subtitle={t('mfaVerify.backupSubtitle')}
      />

      <Input
        className='h-11 text-base md:h-9 md:text-sm text-center tabular-nums tracking-widest'
        type='text'
        value={backupCode}
        placeholder={t('mfaVerify.backupPlaceholder')}
        autoComplete='off'
        spellCheck={false}
        autoFocus
        onChange={(event) => setBackupCode(event.target.value)}
      />
      {error ? <InlineError>{error}</InlineError> : null}

      <Button
        type='submit'
        className='min-h-11 w-full whitespace-normal md:min-h-0 md:whitespace-nowrap'
        disabled={!backupCode.trim()}
        loading={isSubmitting}
        loadingLabel={t('mfaVerify.verifying')}
      >
        {t('mfaVerify.submitButton')}
      </Button>

      {onGoBack ? (
        <div className='flex justify-center'>
          <MfaLinkButton muted onClick={onGoBack}>
            {t('mfaVerify.back')}
          </MfaLinkButton>
        </div>
      ) : null}
    </form>
  )
}
